import { api } from '../utils/api';

class EntityRelationService {
  constructor() {
    // Simple in-memory cache to reduce duplicate requests
    this.cache = new Map();
    this.cacheTimeout = 30000; // 30 seconds
  }

  _getCacheKey(method, ...args) {
    return `${method}:${JSON.stringify(args)}`;
  }

  _getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      console.log(`📦 [ER Service] Cache HIT for ${key}`);
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key); // Expired
    }
    return null;
  }

  _setCache(key, data) {
    this.cache.set(key, { data, timestamp: Date.now() });
    // Auto-cleanup old entries (keep max 100)
    if (this.cache.size > 100) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
  }

  // Internal helper: just call api.get without timeout racing
  // (timeoutMs parameter kept for API compatibility but not enforced)
  async fetchWithTimeout(endpoint, timeoutMs) {
    // Log if request is taking long, but don't abort it
    const startTime = Date.now();
    const warningTimer = setTimeout(() => {
      const elapsed = Date.now() - startTime;
      console.warn(`⚠️ [ER Service] Request to ${endpoint} is taking longer than expected (${elapsed}ms)`);
    }, timeoutMs);

    try {
      const response = await api.get(endpoint);
      clearTimeout(warningTimer);
      const elapsed = Date.now() - startTime;
      if (elapsed > timeoutMs) {
        console.warn(`⚠️ [ER Service] Request to ${endpoint} completed after ${elapsed}ms (expected ${timeoutMs}ms)`);
      }
      return response;
    } catch (error) {
      clearTimeout(warningTimer);
      throw error;
    }
  }
  /**
   * Create a new entity relationship
   * @param {Object} relationData - Relationship data
   * @returns {Promise<Object>} - Created relationship
   */
  async create(relationData) {
    try {
      console.log('🔍 [ER Service] create REQUEST:', {
        relationData,
        endpoint: '/entityrelations',
        fullUrl: `${window.location.origin}/api/entityrelations`
      });
      const response = await api.post('/entityrelations', relationData);
      console.log('✅ [ER Service] create RESPONSE:', response);
      
      // Invalidate cache for affected entities
      this.cache.clear();
      console.log('🗑️ [ER Service] Cache cleared after relation creation');
      
      return response;
    } catch (error) {
      console.error('❌ [ER Service] create ERROR:', {
        relationData,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  /**
   * Create bidirectional entity relationships
   * @param {Object} relationData - Relationship data
   * @returns {Promise<Object>} - Created relationships
   */
  async createBidirectional(relationData) {
    try {
      console.log('🔍 [ER Service] createBidirectional REQUEST:', {
        relationData,
        endpoint: '/entityrelations/bidirectional',
        fullUrl: `${window.location.origin}/api/entityrelations/bidirectional`
      });
      const response = await api.post('/entityrelations/bidirectional', relationData);
      console.log('✅ [ER Service] createBidirectional RESPONSE:', response);
      return response;
    } catch (error) {
      console.error('❌ [ER Service] createBidirectional ERROR:', {
        relationData,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  /**
   * Get related entities for a specific entity
   * @param {string} entityType - Type of entity
   * @param {number} entityID - ID of entity
   * @param {Object} options - Query options
   * @returns {Promise<Object>} - Related entities
   */
  async getRelatedEntities(entityType, entityID, options = {}) {
    const cacheKey = this._getCacheKey('getRelatedEntities', entityType, entityID, options);
    const cached = this._getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const { relationType, direction = 'both', timeoutMs = 20000 } = options;
      const params = new URLSearchParams();
      
      if (relationType) params.append('relationType', relationType);
      if (direction) params.append('direction', direction);

      const qs = params.toString();
      const endpoint = `/entityrelations/${entityType}/${entityID}${qs ? `?${qs}` : ''}`;
      console.log('🔍 [ER Service] getRelatedEntities REQUEST:', {
        entityType,
        entityID,
        options,
        endpoint,
        fullUrl: `${window.location.origin}/api${endpoint}`
      });

      let response;
      try {
        response = await this.fetchWithTimeout(endpoint, timeoutMs);
      } catch (err) {
        console.warn('[ER Service] getRelatedEntities primary endpoint failed, retrying with hyphen route...', err?.message || err);
        // Retry with alternative hyphenated route for compatibility
        const altEndpoint = `/entity-relations/${entityType}/${entityID}${qs ? `?${qs}` : ''}`;
        response = await this.fetchWithTimeout(altEndpoint, timeoutMs + 4000);
      }
      console.log('✅ [ER Service] getRelatedEntities RESPONSE:', {
        entityType,
        entityID,
        responseType: typeof response,
        isArray: Array.isArray(response),
        responseKeys: response ? Object.keys(response) : 'null',
        hasRelations: !!response?.relations,
        relationsCount: response?.relations?.length || 0,
        relations: response?.relations,
        fullResponse: JSON.stringify(response, null, 2).substring(0, 2000) // First 2000 chars
      });
      
      // Debug: Check if response is directly an array
      if (Array.isArray(response)) {
        console.log('🔍 [ER Service] Response is directly an array with', response.length, 'items');
        console.log('🔍 [ER Service] First item:', response[0]);
      }
      
      // Debug: Check if response has relations property
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        console.log('🔍 [ER Service] Response is an object with keys:', Object.keys(response));
        if (response.relations) {
          console.log('🔍 [ER Service] relations property is:', typeof response.relations, Array.isArray(response.relations) ? `array with ${response.relations.length} items` : 'not an array');
          if (Array.isArray(response.relations) && response.relations.length > 0) {
            console.log('🔍 [ER Service] First relation:', response.relations[0]);
          }
        }
      }
      
      this._setCache(cacheKey, response);
      return response;
    } catch (error) {
      console.error('❌ [ER Service] getRelatedEntities ERROR:', {
        entityType,
        entityID,
        options,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  /**
   * Get entities of specific type related to an entity
   * @param {string} entityType - Type of source entity
   * @param {number} entityID - ID of source entity
   * @param {string} targetEntityType - Type of target entities
   * @param {Object} options - Query options
   * @returns {Promise<Object>} - Related entities
   */
  async getRelatedEntitiesByType(entityType, entityID, targetEntityType, options = {}) {
    const cacheKey = this._getCacheKey('getRelatedEntitiesByType', entityType, entityID, targetEntityType, options);
    const cached = this._getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const { relationType, timeoutMs = 20000 } = options;
      const params = new URLSearchParams();
      
      if (relationType) params.append('relationType', relationType);

      const qs = params.toString();
      const endpoint = `/entityrelations/${entityType}/${entityID}/${targetEntityType}/with-data${qs ? `?${qs}` : ''}`;
      console.log('🔍 [ER Service] getRelatedEntitiesByType REQUEST:', {
        entityType,
        entityID,
        targetEntityType,
        options,
        endpoint,
        fullUrl: `${window.location.origin}/api${endpoint}`
      });

      let response;
      try {
        response = await this.fetchWithTimeout(endpoint, timeoutMs);
      } catch (err) {
        console.warn('[ER Service] getRelatedEntitiesByType primary endpoint failed, retrying with hyphen route...', err?.message || err);
        try {
          const altEndpoint = `/entity-relations/${entityType}/${entityID}/${targetEntityType}/with-data${qs ? `?${qs}` : ''}`;
          response = await this.fetchWithTimeout(altEndpoint, timeoutMs + 4000);
        } catch (err2) {
          console.warn('[ER Service] getRelatedEntitiesByType hyphen route failed, falling back to generic list + filter...', err2?.message || err2);
          // Fallback: get all relations (both directions) and filter by type and relationType
          const all = await this.getRelatedEntities(entityType, entityID, { direction: 'both', timeoutMs: timeoutMs + 6000 });
          const allRels = all?.relations || [];
          const filtered = allRels.filter(rel => {
            const srcType = rel.SourceEntityType || rel.sourceEntityType || '';
            const tgtType = rel.TargetEntityType || rel.targetEntityType || '';
            const matchesType = (srcType?.toLowerCase() === targetEntityType.toLowerCase()) || (tgtType?.toLowerCase() === targetEntityType.toLowerCase());
            const relTypeOk = !relationType || (rel.RelationType === relationType || rel.relationType === relationType);
            return matchesType && relTypeOk;
          });
          response = { relations: filtered };
        }
      }
      console.log('✅ [ER Service] getRelatedEntitiesByType RESPONSE:', {
        entityType,
        entityID,
        targetEntityType,
        responseType: typeof response,
        responseKeys: response ? Object.keys(response) : 'null',
        hasRelations: !!response?.relations,
        relationsCount: response?.relations?.length || 0,
        relationsSample: response?.relations?.slice(0, 3),
        fullResponse: response
      });
      
      // Log each relation structure if they exist
      if (response?.relations && response.relations.length > 0) {
        console.log('📋 [ER Service] First relation structure:', {
          firstRelation: response.relations[0],
          firstRelationKeys: Object.keys(response.relations[0]),
          firstRelationTargetID: response.relations[0].TargetEntityID || response.relations[0].targetEntityID
        });
      }
      
      this._setCache(cacheKey, response);
      return response;
    } catch (error) {
      console.error('❌ [ER Service] getRelatedEntitiesByType ERROR:', {
        entityType,
        entityID,
        targetEntityType,
        options,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  /**
   * Update an entity relationship
   * @param {number} relationID - ID of relationship
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} - Updated relationship
   */
  async update(relationID, updateData) {
    try {
      const response = await api.put(`/entityrelations/${relationID}`, updateData);
      return response;
    } catch (error) {
      console.error('❌ Failed to update entity relationship:', error);
      throw error;
    }
  }

  /**
   * Delete an entity relationship
   * @param {number} relationID - ID of relationship
   * @returns {Promise<Object>} - Deletion result
   */
  async delete(relationID, timeoutMs = 5000) {
    try {
      console.log('🗑️ [ER Service] delete REQUEST:', {
        relationID,
        timeoutMs,
        endpoint: `/entityrelations/${relationID}`,
        fullUrl: `${window.location.origin}/api/entityrelations/${relationID}`
      });
      
      const startTime = Date.now();
      
      // Create a timeout promise for the delete operation
      const deletePromise = api.delete(`/entityrelations/${relationID}`);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error(`Delete timeout after ${timeoutMs}ms for relationID ${relationID}`)), timeoutMs)
      );
      
      const response = await Promise.race([deletePromise, timeoutPromise]);
      const elapsed = Date.now() - startTime;
      
      console.log('✅ [ER Service] delete RESPONSE:', {
        relationID,
        response,
        elapsedMs: elapsed,
        success: true
      });
      
      // Invalidate cache after deletion
      this.cache.clear();
      console.log('🗑️ [ER Service] Cache cleared after relation deletion');
      
      return response;
    } catch (error) {
      console.error('❌ [ER Service] delete ERROR:', {
        relationID,
        error: error.message,
        stack: error.stack,
        name: error.name,
        isTimeout: error.message?.includes('timeout')
      });
      throw new Error(`Failed to delete relation ${relationID}: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Delete multiple relationships in parallel with individual error handling
   * @param {Array<number>} relationIDs - Array of relation IDs to delete
   * @param {number} timeoutMs - Timeout per delete operation in milliseconds
   * @returns {Promise<Object>} - Results object with success/failure counts
   */
  async deleteBatch(relationIDs, timeoutMs = 5000) {
    try {
      console.log('🗑️ [ER Service] deleteBatch REQUEST:', {
        count: relationIDs.length,
        relationIDs,
        timeoutMs
      });
      
      const startTime = Date.now();
      
      // Delete all in parallel with individual timeouts
      const deletePromises = relationIDs.map(async (relationID) => {
        try {
          await this.delete(relationID, timeoutMs);
          return { relationID, success: true };
        } catch (error) {
          return { relationID, success: false, error: error.message };
        }
      });
      
      const results = await Promise.all(deletePromises);
      const elapsed = Date.now() - startTime;
      
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      console.log('✅ [ER Service] deleteBatch COMPLETE:', {
        total: relationIDs.length,
        successful: successful.length,
        failed: failed.length,
        elapsedMs: elapsed,
        failures: failed
      });
      
      return {
        total: relationIDs.length,
        successful: successful.length,
        failed: failed.length,
        results,
        elapsedMs: elapsed
      };
    } catch (error) {
      console.error('❌ [ER Service] deleteBatch ERROR:', error);
      throw error;
    }
  }

  /**
   * Get entity relationship statistics
   * @returns {Promise<Object>} - Statistics
   */
  async getStatistics() {
    try {
      const response = await api.get('/entityrelations/statistics');
      return response;
    } catch (error) {
      console.error('❌ Failed to get entity relationship statistics:', error);
      throw error;
    }
  }

  /**
   * Helper method to create Lead-Customer conversion relationship
   * @param {number} leadID - Lead ID
   * @param {number} customerID - Customer ID
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} - Created relationships
   */
  async createLeadCustomerConversion(leadID, customerID, metadata = {}) {
    try {
      const relationData = {
        sourceEntityType: 'Lead',
        sourceEntityID: leadID,
        targetEntityType: 'Customer',
        targetEntityID: customerID,
        relationType: 'converted_from',
        description: 'Lead converted to Customer',
        metadata: {
          conversionDate: new Date().toISOString(),
          originalLeadId: leadID,
          ...metadata
        }
      };

      // Create only one-way relation (Lead -> Customer)
      return await this.create(relationData);
    } catch (error) {
      console.error('❌ Failed to create Lead-Customer conversion relationship:', error);
      throw error;
    }
  }

  /**
   * Helper method to create Lead-Opportunity conversion relationship
   * @param {number} leadID - Lead ID
   * @param {number} opportunityID - Opportunity ID
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} - Created relationship
   */
  async createLeadOpportunityConversion(leadID, opportunityID, metadata = {}) {
    try {
      const relationData = {
        sourceEntityType: 'Lead',
        sourceEntityID: leadID,
        targetEntityType: 'Opportunity',
        targetEntityID: opportunityID,
        relationType: 'converted_to',
        description: 'Lead converted to Opportunity',
        metadata: {
          conversionDate: new Date().toISOString(),
          originalLeadId: leadID,
          ...metadata
        }
      };

      // Create only one-way relation (Lead -> Opportunity)
      return await this.create(relationData);
    } catch (error) {
      console.error('❌ Failed to create Lead-Opportunity conversion relationship:', error);
      throw error;
    }
  }

  /**
   * Helper method to get related opportunity for a converted lead
   * @param {number} leadID - Lead ID
   * @returns {Promise<Object|null>} - Related opportunity or null
   */
  async getLeadOpportunity(leadID) {
    try {
      const response = await this.getRelatedEntitiesByType('Lead', leadID, 'Opportunity');
      const relations = response?.relations || [];
      // Find the converted_to relation
      const conversionRelation = relations.find(r => r.RelationType === 'converted_to' || r.relationType === 'converted_to');
      if (conversionRelation) {
        const opportunityId = conversionRelation.TargetEntityID || conversionRelation.targetEntityID;
        return { opportunityId, relation: conversionRelation };
      }
      // Fallback: return first opportunity if any
      if (relations.length > 0) {
        const rel = relations[0];
        const opportunityId = rel.TargetEntityID || rel.targetEntityID || rel.EntityID || rel.entityId;
        return { opportunityId, relation: rel };
      }
      return null;
    } catch (error) {
      console.error('❌ Failed to get Lead-Opportunity relationship:', error);
      return null;
    }
  }

  /**
   * Helper method to create Salesperson assignment relationship
   * @param {string} entityType - Type of entity
   * @param {number} entityID - ID of entity
   * @param {number} salespersonID - Salesperson ID
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} - Created relationships
   */
  async createSalespersonAssignment(entityType, entityID, salespersonID, metadata = {}) {
    try {
      const relationData = {
        sourceEntityType: entityType,
        sourceEntityID: entityID,
        targetEntityType: 'Salesperson',
        targetEntityID: salespersonID,
        relationType: 'assigned_to',
        description: `${entityType} assigned to Salesperson`,
        metadata: {
          assignmentDate: new Date().toISOString(),
          ...metadata
        }
      };

      return await this.createBidirectional(relationData);
    } catch (error) {
      console.error('❌ Failed to create Salesperson assignment relationship:', error);
      throw error;
    }
  }

  /**
   * Clean up entity relationships when an entity is deleted
   * @param {string} entityType - Type of entity being deleted
   * @param {number} entityID - ID of entity being deleted
   * @returns {Promise<Object>} - Cleanup result
   */
  async cleanupEntityRelations(entityType, entityID) {
    try {
      const response = await api.delete(`/entityrelations/cleanup/${entityType}/${entityID}`);
      return response;
    } catch (error) {
      console.error('❌ Failed to cleanup entity relationships:', error);
      throw error;
    }
  }

  /**
   * Helper method to get related leads for a customer
   * @param {number} customerID - Customer ID
   * @returns {Promise<Array>} - Related leads
   */
  async getCustomerLeads(customerID) {
    try {
      // For one-way relation (Lead -> Customer) using relationType 'converted_from',
      // fetch all relations for the customer and filter by Source=Lead, Target=Customer.
      const all = await this.getRelatedEntities('Customer', customerID, { direction: 'both', timeoutMs: 15000 });
      const relations = Array.isArray(all) ? all : (all?.relations || []);
      const leads = relations.filter((rel) => {
        const srcType = (rel.SourceEntityType || rel.sourceEntityType || '').toString().toLowerCase();
        const tgtType = (rel.TargetEntityType || rel.targetEntityType || '').toString().toLowerCase();
        const relType = rel.RelationType || rel.relationType;
        const tgtId = rel.TargetEntityID || rel.targetEntityID;
        return srcType === 'lead' && tgtType === 'customer' && relType === 'converted_from' && Number(tgtId) === Number(customerID);
      });
      return leads;
    } catch (error) {
      console.error('❌ Failed to get customer leads:', error);
      throw error;
    }
  }

  /**
   * Helper method to get related customers for a lead
   * @param {number} leadID - Lead ID
   * @returns {Promise<Array>} - Related customers
   */
  async getLeadCustomers(leadID) {
    try {
      const response = await this.getRelatedEntitiesByType('Lead', leadID, 'Customer', {
        relationType: 'converted_from'
      });
      return response.relations || [];
    } catch (error) {
      console.error('❌ Failed to get lead customers:', error);
      throw error;
    }
  }

  /**
   * Helper method to get assigned salesperson for an entity
   * @param {string} entityType - Type of entity
   * @param {number} entityID - ID of entity
   * @returns {Promise<Array>} - Assigned salespersons
   */
  async getAssignedSalesperson(entityType, entityID) {
    try {
      const response = await this.getRelatedEntitiesByType(entityType, entityID, 'Salesperson', {
        relationType: 'assigned_to'
      });
      return response.relations || [];
    } catch (error) {
      console.error('❌ Failed to get assigned salesperson:', error);
      throw error;
    }
  }
}

const entityRelationService = new EntityRelationService();
export default entityRelationService;
